import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Calendar as CalendarIcon, Plus, Trash, WhatsappLogo, Clock, List, CalendarBlank } from "@phosphor-icons/react";
import { api, abrirWhatsApp } from "../../utils/api";
import { Button } from "../shared/Button";
import { Input, Textarea } from "../shared/Input";
import { Modal } from "../shared/Modal";
import { Loading, EmptyState } from "../shared/EmptyState";
import { Badge } from "../shared/Badge";
import { formatDate } from "../../utils/api";

export const Agenda = ({ adminToken }) => {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalNuevo, setModalNuevo] = useState(false);
  const [vistaCalendario, setVistaCalendario] = useState(true);
  const [fechaSeleccionada, setFechaSeleccionada] = useState(new Date());
  const [nuevoEvento, setNuevoEvento] = useState({
    fecha: "",
    hora: "",
    titulo: "",
    cliente: "",
    telefono: "",
    direccion: "",
    notas: ""
  });

  useEffect(() => {
    cargarEventos();
  }, []);

  const cargarEventos = async () => {
    try {
      const data = await api.get("/agenda", adminToken);
      setEventos(data);
    } catch (e) {
      console.error("Error:", e);
    } finally {
      setLoading(false);
    }
  };

  const guardarEvento = async () => {
    try {
      await api.post("/agenda", nuevoEvento, adminToken);
      setModalNuevo(false);
      setNuevoEvento({ fecha: "", hora: "", titulo: "", cliente: "", telefono: "", direccion: "", notas: "" });
      cargarEventos();
    } catch (e) {
      console.error("Error:", e);
    }
  };

  const eliminarEvento = async (id) => {
    if (!window.confirm("¿Eliminar evento?")) return;
    try {
      await api.delete(`/agenda/${id}`, adminToken);
      cargarEventos();
    } catch (e) {
      console.error("Error:", e);
    }
  };

  const enviarWhatsApp = (evento) => {
    const mensaje = `Hola ${evento.cliente}, te recordamos tu cita: ${evento.titulo}\nFecha: ${evento.fecha} a las ${evento.hora}\nDirección: ${evento.direccion}`;
    abrirWhatsApp(evento.telefono, mensaje);
  };

  // Obtener eventos del día seleccionado
  const eventosDia = eventos.filter(evento => {
    const fechaEvento = new Date(evento.fecha);
    return fechaEvento.toDateString() === fechaSeleccionada.toDateString();
  });

  // Marcar días con eventos en el calendario
  const tileClassName = ({ date }) => {
    const tieneEvento = eventos.some(evento => {
      const fechaEvento = new Date(evento.fecha);
      return fechaEvento.toDateString() === date.toDateString();
    });
    return tieneEvento ? "has-event" : null;
  };

  if (loading) return <Loading />;

  return (
    <div className="pb-24">
      <style>{`
        .react-calendar {
          width: 100%;
          background: var(--surface-primary);
          border: 1px solid var(--border-subtle);
          border-radius: 12px;
          padding: 16px;
          font-family: inherit;
        }
        .react-calendar__tile--now {
          background: var(--brand-accent);
          color: var(--text-primary);
        }
        .react-calendar__tile--active {
          background: var(--brand-primary) !important;
          color: white;
        }
        .react-calendar__tile.has-event {
          background: var(--info-bg);
          border: 2px solid var(--brand-accent);
        }
        .react-calendar button {
          color: var(--text-primary);
        }
        .react-calendar__month-view__weekdays {
          color: var(--text-secondary);
        }
      `}</style>
      
      <div className="container pt-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-h2 font-display">Agenda de Obras</h1>
            <p className="text-small text-secondary">Gestiona citas y visitas</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant={vistaCalendario ? "primary" : "outline"} 
              onClick={() => setVistaCalendario(true)} 
              icon={<CalendarBlank size={20} />}
              size="sm"
            />
            <Button 
              variant={!vistaCalendario ? "primary" : "outline"} 
              onClick={() => setVistaCalendario(false)} 
              icon={<List size={20} />}
              size="sm"
            />
            <Button variant="accent" onClick={() => setModalNuevo(true)} icon={<Plus size={20} />}>
              Nuevo
            </Button>
          </div>
        </div>

        {vistaCalendario ? (
          <div className="grid md:grid-cols-2 gap-4">
            {/* Calendario */}
            <div className="card p-4">
              <Calendar
                onChange={setFechaSeleccionada}
                value={fechaSeleccionada}
                tileClassName={tileClassName}
                locale="es"
              />
            </div>

            {/* Eventos del día seleccionado */}
            <div className="card p-4">
              <h3 className="text-h3 font-display mb-3">
                {fechaSeleccionada.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
              </h3>
              
              {eventosDia.length === 0 ? (
                <EmptyState
                  icon="📅"
                  title="Sin eventos"
                  description="No hay eventos para este día"
                  action={<Button variant="accent" size="sm" onClick={() => setModalNuevo(true)}>Añadir evento</Button>}
                />
              ) : (
                <div className="space-y-3">
                  {eventosDia.map(evento => (
                    <div key={evento.id} className="card p-3" style={{ background: "var(--surface-secondary)" }}>
                      <div className="font-bold text-small mb-1">{evento.titulo}</div>
                      <div className="text-tiny text-secondary mb-2">{evento.cliente}</div>
                      <div className="flex items-center gap-2 text-tiny mb-2">
                        <Clock size={16} />
                        <span>{evento.hora}</span>
                      </div>
                      {evento.direccion && (
                        <div className="text-tiny text-secondary mb-2">📍 {evento.direccion}</div>
                      )}
                      <div className="flex gap-2 mt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => enviarWhatsApp(evento)}
                          icon={<WhatsappLogo size={14} />}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => eliminarEvento(evento.id)}
                          icon={<Trash size={14} />}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          // Vista lista (original)
          eventos.length === 0 ? (
            <EmptyState
              icon="📅"
              title="Agenda vacía"
              description="No hay eventos programados"
              action={<Button variant="accent" onClick={() => setModalNuevo(true)}>Añadir evento</Button>}
            />
          ) : (
            <div className="space-y-3">
              {eventos.map(evento => (
                <div key={evento.id} className="card p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-bold text-small mb-1">{evento.titulo}</div>
                      <div className="text-tiny text-secondary">{evento.cliente}</div>
                    </div>
                    <Badge variant="accent">
                      {formatDate(evento.fecha)}
                    </Badge>
                  </div>
                  <div className="space-y-1 mb-3">
                    <div className="flex items-center gap-2 text-tiny">
                      <Clock size={16} />
                      <span>{evento.hora}</span>
                    </div>
                    {evento.direccion && (
                      <div className="text-tiny text-secondary">📍 {evento.direccion}</div>
                    )}
                    {evento.notas && (
                      <div className="text-tiny text-secondary">📝 {evento.notas}</div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => enviarWhatsApp(evento)}
                      icon={<WhatsappLogo size={16} />}
                    >
                      WhatsApp
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => eliminarEvento(evento.id)}
                      icon={<Trash size={16} />}
                    >
                      Eliminar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Modal nuevo evento */}
      <Modal isOpen={modalNuevo} onClose={() => setModalNuevo(false)} title="Nuevo Evento">
        <div>
          <Input
            label="Título"
            placeholder="Ej: Visita técnica baño"
            value={nuevoEvento.titulo}
            onChange={(e) => setNuevoEvento(prev => ({ ...prev, titulo: e.target.value }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Fecha"
              type="date"
              value={nuevoEvento.fecha}
              onChange={(e) => setNuevoEvento(prev => ({ ...prev, fecha: e.target.value }))}
            />
            <Input
              label="Hora"
              type="time"
              value={nuevoEvento.hora}
              onChange={(e) => setNuevoEvento(prev => ({ ...prev, hora: e.target.value }))}
            />
          </div>
          <Input
            label="Cliente"
            placeholder="Nombre del cliente"
            value={nuevoEvento.cliente}
            onChange={(e) => setNuevoEvento(prev => ({ ...prev, cliente: e.target.value }))}
          />
          <Input
            label="Teléfono"
            type="tel"
            placeholder="6XX XXX XXX"
            value={nuevoEvento.telefono}
            onChange={(e) => setNuevoEvento(prev => ({ ...prev, telefono: e.target.value }))}
          />
          <Input
            label="Dirección"
            placeholder="Dirección de la obra"
            value={nuevoEvento.direccion}
            onChange={(e) => setNuevoEvento(prev => ({ ...prev, direccion: e.target.value }))}
          />
          <Textarea
            label="Notas"
            placeholder="Notas adicionales"
            value={nuevoEvento.notas}
            onChange={(e) => setNuevoEvento(prev => ({ ...prev, notas: e.target.value }))}
            rows={2}
          />
          <Button
            variant="accent"
            className="btn-full mt-4"
            onClick={guardarEvento}
            disabled={!nuevoEvento.titulo || !nuevoEvento.fecha}
          >
            Guardar evento
          </Button>
        </div>
      </Modal>
    </div>
  );
};